import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../packages/api-client';
import { Button, Input } from '../packages/ui';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';
import { useBotProtection } from '../hooks/useBotProtection';

/**
 * ForgotPasswordPage — asks for an email and kicks off the reset flow.
 *
 * The backend returns 200 whether or not the email exists (to avoid
 * leaking which addresses are registered), so we always show the same
 * "check your email" confirmation regardless of the response.
 */
export function ForgotPasswordPage() {
    usePageTitle('Forgot password');
    const { theme } = useTheme();
    const bot = useBotProtection();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sent, setSent] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setIsLoading(true);
        try {
            await bot.post('/auth/forgot-password', { email });
            setSent(true);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>

                {!sent ? (
                    <>
                        <h1 className="login-title">Reset your password</h1>
                        <p className="login-subtitle">
                            Enter the email linked to your account and we'll send you a link to choose a new password.
                        </p>

                        <form className="login-form" onSubmit={handleSubmit}>
                            <Input
                                id="email"
                                type="email"
                                label="Email address"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                isLoading={isLoading}
                                className="form-button"
                            >
                                Send reset link
                            </Button>
                            {bot.widget}
                        </form>
                    </>
                ) : (
                    <>
                        <h1 className="login-title">Check your email</h1>
                        <p className="login-subtitle">
                            If an account exists for <strong style={{ color: 'var(--ink)' }}>{email}</strong>,
                            we've sent a link to reset your password. The link expires in 1 hour.
                        </p>
                        <p style={{ marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                            Didn't get an email? Check your spam folder, or{' '}
                            <button
                                type="button"
                                onClick={() => setSent(false)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--accent)', fontWeight: 600, fontSize: 'inherit', padding: 0,
                                }}
                            >
                                try a different email
                            </button>.
                        </p>
                    </>
                )}
            </div>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                Remembered it?{' '}
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                    Back to sign in
                </Link>
            </p>
        </div>
    );
}
